import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import api from '@/lib/api';

interface InputData {
  fileName: string;
  fileSize: number;
  mimeType: string;
  language?: string;
}

interface OutputData {
  type?: string;
  originalText?: string;
  processedText?: string;
  error?: string;
}

interface UserOperation {
  id: string;
  traceId?: string;
  createdAt: string;
  operation: string;
  inputType: string;
  inputData: InputData;
  outputType: string | null;
  outputData: OutputData | null;
  status: string;
  user?: {
    id: string;
    username: string;
    email: string;
    name: string;
  };
  application?: {
    id: string;
    appKey: string;
    name: string;
  };
}

interface PaginatedResponse {
  data: UserOperation[];
  meta: {
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  };
}

interface ApiResponse {
  status: string;
  code: number;
  data: UserOperation[];
  meta: {
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  };
}

const OPERATION_LABELS: Record<string, string> = {
  pipeline_clean: '精简整理',
  pipeline_creative: '灵感升华',
  chat: '对话',
  summary: '摘要生成',
  translation: '翻译',
  image_generation: '图片生成',
};

const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  processing: 'bg-blue-100 text-blue-800',
  completed: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
  cancelled: 'bg-gray-100 text-gray-800',
};

const STATUS_LABELS: Record<string, string> = {
  pending: '待处理',
  processing: '处理中',
  completed: '已完成',
  failed: '失败',
  cancelled: '已取消',
};

export default function UserOperationsList() {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['user-operations'],
    queryFn: async () => {
      console.log('[UserOperationsList] Fetching data...');
      const response = await api.get<ApiResponse>('/admin/operations/workflows', {
        params: {
          page: 1,
          pageSize: 50,
        },
      });
      console.log('[UserOperationsList] Full axios response:', response);
      console.log('[UserOperationsList] response.data:', response.data);
      console.log('[UserOperationsList] response.data.data:', response.data.data);
      console.log('[UserOperationsList] response.data.data length:', response.data.data?.length);

      // response.data 是后端返回的完整响应 { status, code, data, meta }
      // 我们需要返回包含 data 和 meta 的对象
      const result = {
        data: response.data.data,  // 操作记录数组
        meta: response.data.meta,  // 分页信息
      };
      console.log('[UserOperationsList] Returning result:', result);
      return result;
    },
    staleTime: 0, // 数据立即过期，下次访问时重新获取
    refetchOnMount: true, // 组件挂载时重新获取
    refetchOnWindowFocus: true, // 窗口聚焦时重新获取
  });

  console.log('[UserOperationsList] Render - data:', data);
  const operations = data?.data || [];
  const total = data?.meta?.total || 0;

  console.log('[UserOperationsList] Render - operations:', operations);
  console.log('[UserOperationsList] Render - operations.length:', operations.length);

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getOperationLabel = (operation: string) => {
    return OPERATION_LABELS[operation] || operation;
  };

  const getStatusBadge = (status: string) => {
    const colors = STATUS_COLORS[status] || 'bg-gray-100 text-gray-800';
    const label = STATUS_LABELS[status] || status;
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded ${colors}`}>
        {label}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {isError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="text-sm text-red-800 flex items-center justify-between">
            <span>加载失败，请刷新页面重试</span>
            <button
              onClick={() => refetch()}
              className="px-3 py-1 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded"
            >
              重试
            </button>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">会员操作记录</h1>
          <p className="mt-1 text-sm text-gray-600">
            查看所有会员的操作历史（只显示业务层面的输入输出）
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="text-sm text-gray-600">
            总计: {total} 条记录
          </div>
          <button
            onClick={() => refetch()}
            className="px-3 py-1 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded"
          >
            刷新数据
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  时间
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  用户
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  应用
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  操作类型
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  输入
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  输出
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  状态
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  操作
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan={8} className="px-6 py-4 text-center text-sm text-gray-500">
                    加载中...
                  </td>
                </tr>
              ) : operations.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-4 text-center text-sm text-gray-500">
                    暂无操作记录
                  </td>
                </tr>
              ) : (
                operations.map((op) => (
                  <tr key={op.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDateTime(op.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div>
                        <div className="font-medium">{op.user?.name || op.user?.username}</div>
                        <div className="text-xs text-gray-500">{op.user?.email}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div>
                        <div className="font-medium">{op.application?.name}</div>
                        <div className="text-xs text-gray-500">{op.application?.appKey}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {getOperationLabel(op.operation)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {op.inputType === 'AUDIO' && op.inputData ? (
                        <div className="flex items-center space-x-2">
                          <span>🎵</span>
                          <div>
                            <div className="text-xs font-medium">{op.inputData.fileName}</div>
                            <div className="text-xs text-gray-500">
                              {formatFileSize(op.inputData.fileSize)}
                            </div>
                          </div>
                        </div>
                      ) : op.inputType === 'TEXT' ? (
                        <div className="flex items-center space-x-2">
                          <span>📝</span>
                          <div className="text-xs truncate max-w-xs">
                            {JSON.stringify(op.inputData)?.substring(0, 50)}...
                          </div>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-500">{op.inputType}</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {op.status === 'failed' ? (
                        <div className="text-red-600 text-xs truncate max-w-xs">
                          {op.outputData?.error || '处理失败'}
                        </div>
                      ) : op.outputType === 'TEXT' && op.outputData?.processedText ? (
                        <div className="text-xs text-gray-600 truncate max-w-xs">
                          {op.outputData.processedText.substring(0, 50)}...
                        </div>
                      ) : op.status === 'processing' || op.status === 'pending' ? (
                        <span className="text-xs text-blue-600">处理中...</span>
                      ) : (
                        <span className="text-xs text-gray-500">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {getStatusBadge(op.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {op.traceId ? (
                        <Link
                          to={`/user-operations/${op.traceId}`}
                          className="text-blue-600 hover:text-blue-800 font-medium"
                        >
                          查看详情
                        </Link>
                      ) : (
                        <span className="text-gray-400" title="无追踪信息">
                          无详情
                        </span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* 分页 */}
        {data && data.totalPages > 1 && (
          <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                显示第 {(data.page - 1) * data.pageSize + 1} 到{' '}
                {Math.min(data.page * data.pageSize, data.total)} 条，共 {data.total} 条
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
